import { z } from "zod";
import { Role } from "./auth.js";
import { MemberAccess } from "./users.js";

/*
 * API access (docs/api-keys.md): organisation keys for scripts, CI and AI
 * agents, service accounts that own keys, policies that say what a key may
 * call, and the organisation's switches of the "Endpoints" page.
 */

/** Built-in policy templates; their statements live in the API (`access/keys.ts`). */
export const POLICY_TEMPLATES = [
  "read_only",
  "monitoring",
  "operator",
  "developer",
  "full_access",
  "ci_deploy",
] as const;
export const PolicyTemplate = z.enum(POLICY_TEMPLATES);
export type PolicyTemplate = z.infer<typeof PolicyTemplate>;

export const RiskClass = z.enum([
  "read",
  "operate",
  "change",
  "create",
  "destroy",
  "secret",
  "admin",
]);
export type RiskClass = z.infer<typeof RiskClass>;

/**
 * An action pattern: an id (`servers.reboot`), a wildcard (`servers.*`), a
 * risk class (`risk:destroy`) or a wildcard of one class (`servers.*@operate`).
 * The API refuses a pattern that matches no action (`unknown_action`).
 */
export const ActionPattern = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(/^(risk:[a-z]+|[a-z0-9_.*]+(@[a-z]+)?)$/);

/** One statement of a policy (`access/policy.ts`); `projects` absent: organisation-level actions. */
export const PolicyStatement = z.object({
  effect: z.enum(["allow", "deny", "require_approval"]),
  actions: z.array(ActionPattern).min(1).max(100),
  /** `"*"`: every project the key may reach; a list: those of them. */
  projects: z.union([z.literal("*"), z.array(z.uuid()).max(500)]).optional(),
  reason: z
    .string()
    .regex(/^[a-z_]{1,64}$/)
    .optional(),
  detail: z.string().max(200).optional(),
});
export type PolicyStatement = z.infer<typeof PolicyStatement>;

export const PolicyStatements = z.array(PolicyStatement).min(1).max(50);

/** What a key's policy is: a template, a policy of the library, or its own statements. */
export const KeyPolicy = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("template"), template: PolicyTemplate }),
  z.object({ kind: z.literal("library"), policyId: z.uuid() }),
  z.object({ kind: z.literal("custom"), statements: PolicyStatements }),
]);
export type KeyPolicy = z.infer<typeof KeyPolicy>;

export const KeyOwner = z.discriminatedUnion("kind", [
  /** The person creating the key. */
  z.object({ kind: z.literal("me") }),
  z.object({ kind: z.literal("service_account"), id: z.uuid() }),
]);
export type KeyOwner = z.infer<typeof KeyOwner>;

/** An IPv4/IPv6 address or CIDR range; validated by the API (`ip_invalid`). */
const IpRange = z.string().trim().min(2).max(64);

export const KeyStatus = z.enum(["active", "expired", "revoked"]);
export type KeyStatus = z.infer<typeof KeyStatus>;

/** A key as the panel lists it; the secret itself is never returned again. */
export const ApiKey = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  /** `csk_<id>_…<last four>`: enough to recognise the key, useless to call with. */
  hint: z.string(),
  owner: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("user"), id: z.uuid(), name: z.string() }),
    z.object({ kind: z.literal("service_account"), id: z.uuid(), name: z.string() }),
  ]),
  createdBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  /** `"all"`: every project the owner may reach, also future ones. */
  projects: z.union([z.literal("all"), z.array(z.object({ id: z.uuid(), name: z.string() }))]),
  policy: KeyPolicy,
  /** The policy's display name: the template key or the library policy's name. */
  policyName: z.string(),
  allowSecret: z.boolean(),
  ipAllowList: z.array(z.string()),
  /** Requests per minute; null: the organisation's default. */
  rateLimitPerMinute: z.number().int().positive().nullable(),
  expiresAt: z.iso.datetime().nullable(),
  status: KeyStatus,
  revokedAt: z.iso.datetime().nullable(),
  rotatedAt: z.iso.datetime().nullable(),
  /** Until when the secret before the last rotation still works. */
  previousSecretValidUntil: z.iso.datetime().nullable(),
  lastUsed: z
    .object({ at: z.iso.datetime(), ip: z.string().nullable(), client: z.string().nullable() })
    .nullable(),
  requestCount: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
});
export type ApiKey = z.infer<typeof ApiKey>;

export const KEY_LIFETIME_DEFAULT_DAYS = 90;

export const CreateApiKeyRequest = z.object({
  name: z.string().trim().min(1).max(64),
  description: z.string().trim().max(500).default(""),
  owner: KeyOwner.default({ kind: "me" }),
  projects: z.union([z.literal("all"), z.array(z.uuid()).min(1).max(500)]),
  policy: KeyPolicy,
  allowSecret: z.boolean().default(false),
  /** Days from now; null: never expires (only without an organisation maximum, administrators). */
  expiresInDays: z.number().int().min(1).max(3650).nullable().default(KEY_LIFETIME_DEFAULT_DAYS),
  ipAllowList: z.array(IpRange).max(50).default([]),
  rateLimitPerMinute: z.number().int().min(1).max(10_000).nullable().default(null),
});
export type CreateApiKeyRequest = z.input<typeof CreateApiKeyRequest>;

export const UpdateApiKeyRequest = z
  .object({
    name: z.string().trim().min(1).max(64),
    description: z.string().trim().max(500),
    projects: z.union([z.literal("all"), z.array(z.uuid()).min(1).max(500)]),
    policy: KeyPolicy,
    allowSecret: z.boolean(),
    ipAllowList: z.array(IpRange).max(50),
    rateLimitPerMinute: z.number().int().min(1).max(10_000).nullable(),
  })
  .partial();
export type UpdateApiKeyRequest = z.infer<typeof UpdateApiKeyRequest>;

export const RotateApiKeyRequest = z.object({
  /** How long the old secret keeps working, 0 (at once) to 7 days. */
  graceHours: z.number().int().min(0).max(168).default(24),
});
export type RotateApiKeyRequest = z.input<typeof RotateApiKeyRequest>;

/** A new or rotated key with its token: the only time the token is shown. */
export const CreatedApiKey = z.object({ key: ApiKey, token: z.string() });
export type CreatedApiKey = z.infer<typeof CreatedApiKey>;

export const ServiceAccount = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  role: Role,
  owner: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  projects: z.array(
    z.object({ projectId: z.uuid(), projectName: z.string(), access: MemberAccess }),
  ),
  disabled: z.boolean(),
  keyCount: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
});
export type ServiceAccount = z.infer<typeof ServiceAccount>;

const ServiceAccountFields = z.object({
  name: z.string().trim().min(1).max(64),
  description: z.string().trim().max(500),
  role: Role,
  ownerUserId: z.uuid(),
  /** Ignored for administrators, who reach every project. */
  projects: z.array(z.object({ projectId: z.uuid(), access: MemberAccess })).max(500),
  disabled: z.boolean(),
});

export const CreateServiceAccountRequest = ServiceAccountFields.extend({
  description: ServiceAccountFields.shape.description.default(""),
  role: Role.default("member"),
  projects: ServiceAccountFields.shape.projects.default([]),
}).omit({ disabled: true });
export type CreateServiceAccountRequest = z.input<typeof CreateServiceAccountRequest>;

export const UpdateServiceAccountRequest = ServiceAccountFields.partial();
export type UpdateServiceAccountRequest = z.infer<typeof UpdateServiceAccountRequest>;

export const ApiPolicy = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  statements: PolicyStatements,
  /** Keys that use it; a policy in use cannot be removed. */
  keyCount: z.number().int().nonnegative(),
  updatedAt: z.iso.datetime(),
});
export type ApiPolicy = z.infer<typeof ApiPolicy>;

export const PolicyTemplateView = z.object({
  template: PolicyTemplate,
  statements: PolicyStatements,
});
export type PolicyTemplateView = z.infer<typeof PolicyTemplateView>;

export const ApiPolicies = z.object({
  templates: z.array(PolicyTemplateView),
  policies: z.array(ApiPolicy),
});
export type ApiPolicies = z.infer<typeof ApiPolicies>;

export const SaveApiPolicyRequest = z.object({
  name: z.string().trim().min(1).max(64),
  description: z.string().trim().max(500).default(""),
  statements: PolicyStatements,
});
export type SaveApiPolicyRequest = z.input<typeof SaveApiPolicyRequest>;

/** One action a key can be given, for the policy editor's matrix. */
export const ActionInfo = z.object({
  id: z.string(),
  /** The resource group: the part before the first dot (`servers`). */
  group: z.string(),
  risk: RiskClass,
  scope: z.enum(["project", "organization"]),
});
export type ActionInfo = z.infer<typeof ActionInfo>;

/** The organisation's API switches and defaults, plus where the API lives. */
export const ApiSettings = z.object({
  apiEnabled: z.boolean(),
  defaultRateLimitPerMinute: z.number().int().min(1).max(10_000),
  /** null: no maximum. */
  maxKeyLifetimeDays: z.number().int().min(1).max(3650).nullable(),
  allowSecretForKeys: z.boolean(),
  /** Whether keys may connect to the MCP server (docs/mcp.md); the REST API is `apiEnabled`. */
  mcpEnabled: z.boolean(),
  restBaseUrl: z.string(),
  openapiUrl: z.string(),
  /** The MCP server's Streamable HTTP endpoint. */
  mcpUrl: z.string(),
});
export type ApiSettings = z.infer<typeof ApiSettings>;

export const UpdateApiSettingsRequest = ApiSettings.pick({
  apiEnabled: true,
  defaultRateLimitPerMinute: true,
  maxKeyLifetimeDays: true,
  allowSecretForKeys: true,
  mcpEnabled: true,
}).partial();
export type UpdateApiSettingsRequest = z.infer<typeof UpdateApiSettingsRequest>;

/** One audit entry of a key: a call it made, or a change made to it. */
export const KeyActivity = z.object({
  at: z.iso.datetime(),
  action: z.string(),
  outcome: z.enum(["success", "denied", "failed"]),
  target: z.string().nullable(),
  key: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  /** The person who acted, for changes made in the panel. */
  user: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  ip: z.string().nullable(),
  /** Why a call was refused (`errors.reasons.<reason>`). */
  reason: z.string().nullable(),
  /** The MCP tool of an `mcp.tool_call` entry (docs/mcp.md). */
  tool: z.string().nullable(),
});
export type KeyActivity = z.infer<typeof KeyActivity>;
